import Service, { service } from '@ember/service';
import type Model from '@ember-data/model';
import type Store from '@ember-data/store';
import { pluralize } from 'ember-inflector';
import type BadgeModel from 'garaje/models/badge';
import type ConnectInviteModel from 'garaje/models/connect-invite';
import type InviteModel from 'garaje/models/invite';
import type PrinterModel from 'garaje/models/printer';
import type PropertyBadgeModel from 'garaje/models/property-badge';
import type PropertyPrinterModel from 'garaje/models/property-printer';
import { generateBadgePrintEventId } from 'garaje/utils/badge-print-event-id';
import urlBuilder from 'garaje/utils/url-builder';

import type AjaxService from './ajax';
import type MetricsService from './metrics';

type ModelPartial = { type: string; id: string };

export interface PrintOptions {
  badge: PropertyBadgeModel | BadgeModel | ModelPartial;
  printer: PrinterModel | PropertyPrinterModel | ModelPartial;
  visit: ConnectInviteModel | InviteModel | ModelPartial;
  badgePrintEventId?: string;
}

export default class BadgePrintingService extends Service {
  @service declare ajax: AjaxService;
  @service declare store: Store;
  @service declare metrics: MetricsService;

  async print(jobs: PrintOptions | PrintOptions[]): Promise<void> {
    const startTime = Date.now();
    jobs = Array.isArray(jobs) ? jobs : [jobs];

    // Extract badgePrintEventId from the first job or generate one
    const badgePrintEventId = jobs[0]?.badgePrintEventId || generateBadgePrintEventId();
    const jobCount = jobs.length;

    // Extract safe contextual identifiers
    const firstJob = jobs[0];
    // Safe access to location/company IDs with type assertion
    const location_id = firstJob
      ? ((firstJob.visit as { locationId?: string }).locationId ??
        (firstJob.printer as { locationId?: string }).locationId ??
        null)
      : null;
    const company_id = firstJob
      ? ((firstJob.visit as { companyId?: string }).companyId ??
        (firstJob.printer as { companyId?: string }).companyId ??
        null)
      : null;

    // Initial metrics log
    this.metrics.logMonitorEvent('Badge print initiated', {
      badge_print_event_id: badgePrintEventId,
      job_count: jobCount,
      location_id,
      company_id,
      user_action_timestamp: startTime,
      visits: jobs.map((job) => ({
        visit_id: job.visit.id,
        visit_type: (job.visit as { type?: string }).type || 'unknown',
        printer_id: job.printer.id,
      })),
      source: 'garaje_badge_printing_service',
    });

    const data = {
      data: jobs.map((job) => {
        return {
          type: 'badge-print-jobs',
          attributes: {
            badge_print_event_id: badgePrintEventId,
            initiated_timestamp: startTime,
          },
          relationships: {
            badge: this.#getRelData(job.badge),
            printer: this.#getRelData(job.printer),
            visit: this.#getRelData(job.visit),
          },
        };
      }),
      meta: { badge_print_event_id: badgePrintEventId },
    };

    try {
      // Track API call timing
      const apiCallStart = Date.now();
      await this.ajax.post(urlBuilder.v3.printers.badgePrintJobs(), {
        headers: {
          accept: 'application/vnd.api+json',
        },
        contentType: 'application/vnd.api+json',
        data: data,
      });
      const apiCallDuration = Date.now() - apiCallStart;

      // Success metrics
      this.metrics.logMonitorEvent('Badge print API call completed', {
        badge_print_event_id: badgePrintEventId,
        location_id,
        company_id,
        api_call_duration_ms: apiCallDuration,
        total_duration_ms: Date.now() - startTime,
        job_count: jobCount,
        status: 'api_success',
        source: 'garaje_badge_printing_service',
      });
    } catch (error) {
      const totalDuration = Date.now() - startTime;

      // Error metrics
      this.metrics.logMonitorError({
        event: 'Badge print API call failed',
        debugExtras: {
          badge_print_event_id: badgePrintEventId,
          location_id,
          company_id,
          total_duration_ms: totalDuration,
          job_count: jobCount,
          status: 'api_error',
          source: 'garaje_badge_printing_service',
        },
        error,
      });

      throw error;
    }
  }

  #getRelData(model: Model | ModelPartial) {
    const type = 'isNew' in model ? (<typeof Model>model.constructor).modelName : model.type;
    return { data: { type: pluralize(`${type}`), id: model.id } };
  }
}
